import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApps } from '@/contexts/AppsContext';
import { billingApi } from '@/services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { formatNumber, formatBytes } from '@/lib/utils';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Activity, Zap, Database, Clock, ArrowRight, Plus, Shield, Users, Boxes } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

export default function DashboardPage() {
  const { apps, currentApp } = useApps();
  const { isAdmin, user } = useAuth();
  const [usage, setUsage] = useState(null);
  const [usageHistory, setUsageHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUsage = async () => {
      if (!currentApp) {
        setLoading(false);
        return;
      }

      // Billing API routes are not yet deployed — skip the calls
      // to avoid CORS errors on non-existent endpoints.
      // TODO: Re-enable when billing backend is implemented.
      // try {
      //   const [usageRes, historyRes] = await Promise.all([
      //     billingApi.getUsage(currentApp.appId),
      //     billingApi.getUsageHistory(currentApp.appId, 6),
      //   ]);
      //   setUsage(usageRes.data.data);
      //   setUsageHistory(historyRes.data.data?.history || []);
      // } catch (err) {
      //   console.error('Failed to fetch usage:', err);
      // }
      setLoading(false);
    };

    fetchUsage();
  }, [currentApp]);

  // No apps state
  if (apps.length === 0 && !loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="text-center space-y-4">
          <div className="h-20 w-20 rounded-full bg-muted mx-auto flex items-center justify-center">
            <Plus className="h-10 w-10 text-muted-foreground" />
          </div>
          <h2 className="text-2xl font-semibold">Create your first app</h2>
          <p className="text-muted-foreground max-w-md">
            Get started by creating an app to begin using the TruContext API.
          </p>
          <Button asChild>
            <Link to="/apps/new">
              <Plus className="mr-2 h-4 w-4" />
              Create App
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  // Prepare chart data
  const chartData = usageHistory.map((item) => ({
    name: item.periodKey,
    apiCalls: item.apiCalls || 0,
    tokens: item.llmTokens || 0,
  })).reverse();

  const stats = [
    {
      title: 'API Calls',
      value: formatNumber(usage?.apiCalls || 0),
      limit: usage?.limits?.apiCalls,
      percent: usage?.apiCallsPercent || 0,
      icon: Activity,
      color: 'text-blue-500',
    },
    {
      title: 'LLM Tokens',
      value: formatNumber(usage?.llmTokens || 0),
      limit: usage?.limits?.llmTokens,
      percent: usage?.limits?.llmTokens
        ? ((usage?.llmTokens || 0) / usage.limits.llmTokens) * 100
        : 0,
      icon: Zap,
      color: 'text-yellow-500',
    },
    {
      title: 'Storage',
      value: formatBytes(usage?.storageBytes || 0),
      limit: usage?.limits?.storageGb ? `${usage.limits.storageGb} GB` : null,
      percent: usage?.storagePercent || 0,
      icon: Database,
      color: 'text-green-500',
    },
    {
      title: 'Processing',
      value: `${(usage?.processingMinutes || 0).toFixed(1)} min`,
      limit: usage?.limits?.processingMinutes
        ? `${usage.limits.processingMinutes} min`
        : null,
      percent: usage?.limits?.processingMinutes
        ? ((usage?.processingMinutes || 0) / usage.limits.processingMinutes) * 100
        : 0,
      icon: Clock,
      color: 'text-purple-500',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Welcome Card */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}
                {isAdmin && (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 border-primary/50 text-primary">
                    <Shield className="h-2.5 w-2.5 mr-0.5" />
                    System Admin
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>
                {isAdmin
                  ? `Platform overview \u2014 ${apps.length} app${apps.length !== 1 ? 's' : ''} on the platform. Viewing: ${currentApp?.name || 'none selected'}.`
                  : `Here's what's happening with ${currentApp?.name || 'your app'} this month.`
                }
              </CardDescription>
            </div>
            {currentApp && (
              <Badge variant={currentApp.status === 'active' ? 'success' : 'secondary'}>
                {currentApp.status}
              </Badge>
            )}
          </div>
        </CardHeader>
      </Card>

      {/* Admin Platform Overview */}
      {isAdmin && (
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Apps
              </CardTitle>
              <Boxes className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{apps.length}</div>
              <p className="text-xs text-muted-foreground">on the platform</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Unique Owners
              </CardTitle>
              <Users className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {new Set(apps.map(a => a.adminEmail).filter(Boolean)).size}
              </div>
              <p className="text-xs text-muted-foreground">distinct admin emails</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Active Apps
              </CardTitle>
              <Activity className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {apps.filter(a => a.status === 'active').length}
              </div>
              <p className="text-xs text-muted-foreground">
                of {apps.length} total
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <Card key={stat.title}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {stat.title}
              </CardTitle>
              <stat.icon className={`h-4 w-4 ${stat.color}`} />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stat.value}</div>
              {stat.limit && (
                <>
                  <p className="text-xs text-muted-foreground">
                    of {typeof stat.limit === 'number' ? formatNumber(stat.limit) : stat.limit}
                  </p>
                  <Progress value={Math.min(stat.percent, 100)} className="mt-2 h-1" />
                </>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Usage Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Usage Over Time</CardTitle>
          <CardDescription>API calls and token usage for the past 6 months</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="h-[300px] flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="colorCalls" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorTokens" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(47, 100%, 50%)" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="hsl(47, 100%, 50%)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="name" className="text-xs" />
                  <YAxis className="text-xs" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="apiCalls"
                    stroke="hsl(var(--primary))"
                    fillOpacity={1}
                    fill="url(#colorCalls)"
                    name="API Calls"
                  />
                  <Area
                    type="monotone"
                    dataKey="tokens"
                    stroke="hsl(47, 100%, 50%)"
                    fillOpacity={1}
                    fill="url(#colorTokens)"
                    name="LLM Tokens"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card className="hover:border-primary/50 transition-colors cursor-pointer">
          <Link to="/keys">
            <CardHeader>
              <CardTitle className="text-base flex items-center justify-between">
                Manage API Keys
                <ArrowRight className="h-4 w-4" />
              </CardTitle>
              <CardDescription>Create or revoke API keys for your app</CardDescription>
            </CardHeader>
          </Link>
        </Card>
        <Card className="hover:border-primary/50 transition-colors cursor-pointer">
          <Link to="/billing">
            <CardHeader>
              <CardTitle className="text-base flex items-center justify-between">
                View Billing
                <ArrowRight className="h-4 w-4" />
              </CardTitle>
              <CardDescription>Check your usage and upgrade your plan</CardDescription>
            </CardHeader>
          </Link>
        </Card>
        <Card className="hover:border-primary/50 transition-colors cursor-pointer">
          <Link to="/documentation">
            <CardHeader>
              <CardTitle className="text-base flex items-center justify-between">
                Documentation
                <ArrowRight className="h-4 w-4" />
              </CardTitle>
              <CardDescription>Learn how to integrate TruContext</CardDescription>
            </CardHeader>
          </Link>
        </Card>
      </div>
    </div>
  );
}
